// ============================================================================
// Sensors Modals Module
// ============================================================================
// Modal functions for sensor CRUD operations and export

import { showEditSensorModal } from './sensor-edit.js';
import { loadSensorsData } from './sensors.js';
import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess } from '../core/ui.js';
import { auth } from '../core/auth.js';
import { t } from '../core/i18n.js';

// ============================================================================
// Modal Helper Function
// ============================================================================

function closeModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.classList.add('hidden');
        modal.innerHTML = '';
    }
}

// ============================================================================
// Create Sensor Modal & Functions
// ============================================================================

export async function showCreateSensorModal() {
    const modalHtml = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div class="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h3 class="text-lg font-medium text-gray-900">${t('sensors.add')}</h3>
                </div>
                <form onsubmit="handleCreateSensor(event)" class="px-6 py-4 space-y-4">
                    <!-- Sensor Name -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('sensors.name')} <span class="text-red-500">*</span>
                        </label>
                        <input type="text" 
                               id="create-sensor-name" 
                               required
                               class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                               placeholder="f.eks., Temperatur Sensor 1">
                    </div>

                    <!-- Sensor ID -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('sensors.sensorId')} <span class="text-red-500">*</span>
                        </label>
                        <input type="text" 
                               id="create-sensor-id" 
                               required
                               class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                               placeholder="f.eks., TEMP001">
                        <p class="mt-1 text-sm text-gray-500">Dette skal være et unikt ID for sensoren</p>
                    </div>

                    <!-- Sensor Type -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('sensors.sensorType')} <span class="text-red-500">*</span>
                        </label>
                        <select id="create-sensor-type" 
                                required
                                onchange="window.onCreateSensorTypeChange(this.value)"
                                class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                            <option value="">${t('sensors.selectSensorType')}</option>
                            <option value="temperature">${t('sensors.typeTemperature')}</option>
                            <option value="temperature_humidity">${t('sensors.typeTempHumidity')}</option>
                            <option value="temp_humidity">${t('sensors.typeTempHumidityBle')}</option>
                            <option value="tracker">${t('sensors.typeTracker')}</option>
                            <option value="gateway">${t('sensors.typeGateway')}</option>
                            <option value="card_reader">${t('sensors.typeCardReader')}</option>
                            <option value="activity">${t('sensors.typeActivity')}</option>
                            <option value="light">${t('sensors.typeLight')}</option>
                        </select>
                    </div>

                    <!-- BLE MAC Address (shown only for BLE temp/humidity sensors) -->
                    <div id="create-sensor-mac-wrapper" class="hidden">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('sensors.macAddress')}
                        </label>
                        <input type="text"
                               id="create-sensor-mac"
                               class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                               placeholder="f.eks., D0:83:D4:01:72:74">
                        <p class="mt-1 text-sm text-gray-500">${t('sensors.macAddressHint')}</p>
                    </div>

                    <!-- Account Tree -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            ${t('locations.selectAccount')} <span class="text-red-500">*</span>
                        </label>
                        <div id="sensor-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-white">
                            <div class="text-gray-400 text-sm">${t('sensors.loadingAccounts')}</div>
                        </div>
                        <input type="hidden" id="create-sensor-account" name="account_id" required>
                    </div>

                    <!-- Status -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('common.status')}
                        </label>
                        <select id="create-sensor-status" 
                                class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                            <option value="active">${t('common.active')}</option>
                            <option value="inactive">${t('common.inactive')}</option>
                            <option value="maintenance">${t('sensors.statusMaintenance')}</option>
                        </select>
                    </div>

                    <!-- Actions -->
                    <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button type="button" 
                                onclick="closeModal()"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            ${t('common.cancel')}
                        </button>
                        <button type="submit"
                                class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                            ${t('sensors.createButton')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    `;

    // Show modal
    const modal = document.getElementById('modal');
    modal.innerHTML = modalHtml;
    modal.classList.remove('hidden');

    // Load accounts tree
    await loadAccountsForSensorModal('sensor-account-tree-container', 'create-sensor-account');
}

export async function handleCreateSensor(event) {
    event.preventDefault();

    const name = document.getElementById('create-sensor-name').value.trim();
    const sensorId = document.getElementById('create-sensor-id').value.trim();
    const sensorType = document.getElementById('create-sensor-type').value;
    const status = document.getElementById('create-sensor-status').value || 'active';
    const accountId = document.getElementById('create-sensor-account').value;
    const macAddress = document.getElementById('create-sensor-mac')?.value.trim();

    // Validate required fields
    if (!name || !sensorId || !sensorType || !accountId) {
        showError(t('sensors.requiredFieldsError'));
        return;
    }

    // Disable submit button to prevent double submission
    const submitButton = event.target.querySelector('button[type="submit"]');
    if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Creating...';
    }

    try {
        const sensorData = {
            name,
            sensor_id: sensorId,
            sensor_type: sensorType,
            status,
            association_type: 'account',
            account_id: accountId,
            location_id: null,
            asset_id: null
        };

        // Store the BLE MAC (beacon mode) when provided for BLE temp/humidity sensors
        if (sensorType === 'temp_humidity' && macAddress) {
            sensorData.configuration = { mac_address: macAddress.toLowerCase() };
        }

        console.log('Creating sensor with data:', sensorData);

        const response = await fetch(`${API_BASE_URL}/sensors`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(sensorData)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to create sensor');
        }

        const result = await response.json();
        console.log('Sensor created successfully:', result);

        // Close modal
        closeModal();

        // Show success message
        showSuccess(t('sensors.createSuccess'));

        // Refresh the sensors list
        await loadSensorsData(true);

    } catch (error) {
        console.error('Error creating sensor:', error);
        showError(error.message || t('sensors.createError'));
    } finally {
        // Re-enable submit button
        if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = t('sensors.createButton');
        }
    }
}

// ============================================================================
// Import Sensors Modal & Functions (SuperAdmin only)
// ============================================================================

export async function showImportSensorsModal() {
    const modalHtml = `
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div class="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h3 class="text-lg font-medium text-gray-900">${t('sensors.importTitle')}</h3>
                    <p class="mt-1 text-sm text-gray-500">${t('sensors.importDescription')}</p>
                </div>
                <form onsubmit="handleImportSensors(event)" class="px-6 py-4 space-y-4">
                    <!-- Excel File -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('sensors.importFile')} <span class="text-red-500">*</span>
                        </label>
                        <input type="file"
                               id="import-sensor-file"
                               accept=".xlsx"
                               required
                               class="w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100">
                        <p class="mt-1 text-sm text-gray-500">${t('sensors.importFileHint')}</p>
                    </div>

                    <!-- Sensor Type -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            ${t('sensors.sensorType')} <span class="text-red-500">*</span>
                        </label>
                        <select id="import-sensor-type"
                                required
                                class="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500">
                            <option value="">${t('sensors.selectSensorType')}</option>
                            <option value="temperature">${t('sensors.typeTemperature')}</option>
                            <option value="temperature_humidity">${t('sensors.typeTempHumidity')}</option>
                            <option value="temp_humidity">${t('sensors.typeTempHumidityBle')}</option>
                            <option value="tracker">${t('sensors.typeTracker')}</option>
                            <option value="gateway">${t('sensors.typeGateway')}</option>
                            <option value="card_reader">${t('sensors.typeCardReader')}</option>
                            <option value="activity">${t('sensors.typeActivity')}</option>
                            <option value="light">${t('sensors.typeLight')}</option>
                        </select>
                    </div>

                    <!-- Account Tree -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">
                            ${t('locations.selectAccount')} <span class="text-red-500">*</span>
                        </label>
                        <div id="import-sensor-account-tree-container" class="border border-gray-300 rounded-md p-3 max-h-64 overflow-y-auto bg-white">
                            <div class="text-gray-400 text-sm">${t('sensors.loadingAccounts')}</div>
                        </div>
                        <input type="hidden" id="import-sensor-account" name="account_id" required>
                    </div>

                    <!-- Result summary (populated after import) -->
                    <div id="import-sensor-result" class="hidden text-sm rounded-md p-3"></div>

                    <!-- Actions -->
                    <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button type="button"
                                onclick="closeModal()"
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            ${t('common.cancel')}
                        </button>
                        <button type="submit"
                                class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
                            ${t('sensors.importSubmit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    `;

    const modal = document.getElementById('modal');
    modal.innerHTML = modalHtml;
    modal.classList.remove('hidden');

    await loadAccountsForSensorModal('import-sensor-account-tree-container', 'import-sensor-account');
}

export async function handleImportSensors(event) {
    event.preventDefault();

    const fileInput = document.getElementById('import-sensor-file');
    const file = fileInput?.files?.[0];
    const sensorType = document.getElementById('import-sensor-type').value;
    const accountId = document.getElementById('import-sensor-account').value;

    if (!file || !sensorType || !accountId) {
        showError(t('sensors.importRequiredError'));
        return;
    }

    const submitButton = event.target.querySelector('button[type="submit"]');
    if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = t('sensors.importing');
    }

    try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('sensor_type', sensorType);
        formData.append('account_id', accountId);

        const response = await fetch(`${API_BASE_URL}/sensors/bulk-import`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            },
            body: formData
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || t('sensors.importError'));
        }

        const result = await response.json();

        // Show result summary inside the modal
        const resultBox = document.getElementById('import-sensor-result');
        if (resultBox) {
            resultBox.className = 'text-sm rounded-md p-3 bg-green-50 text-green-800 border border-green-200';
            resultBox.innerHTML =
                `${t('sensors.importResultImported')}: <strong>${result.imported_count}</strong><br>` +
                `${t('sensors.importResultSkipped')}: <strong>${result.skipped_count}</strong>`;
            resultBox.classList.remove('hidden');
        }

        showSuccess(t('sensors.importSuccess').replace('{count}', result.imported_count));

        await loadSensorsData(true);

    } catch (error) {
        console.error('Error importing sensors:', error);
        showError(error.message || t('sensors.importError'));
    } finally {
        if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = t('sensors.importSubmit');
        }
    }
}

// ============================================================================
// Account Tree Helper Functions for Sensor Modals
// ============================================================================

export async function loadAccountsForSensorModal(containerId, hiddenInputId, selectedAccountId = null) {
    try {
        const response = await apiCall('/accounts/hierarchy', {
            method: 'GET',
            headers: auth.getAuthHeaders()
        });
        
        const container = document.getElementById(containerId);
        if (!container) return;
        
        if (response && response.accounts_tree) {
            container.innerHTML = renderSensorAccountTree(response.accounts_tree, selectedAccountId);
            
            // If there's a selected account, make sure it's checked
            if (selectedAccountId) {
                const radio = container.querySelector(`input[value="${selectedAccountId}"]`);
                if (radio) {
                    radio.checked = true;
                }
            }
        } else {
            container.innerHTML = `<div class="text-gray-500 text-sm">${t('sensors.noAccountsFound')}</div>`;
        }
            
    } catch (error) {
        console.error('Error loading accounts:', error);
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = `<div class="text-red-500 text-sm">${t('sensors.errorLoadingAccounts')}</div>`;
        }
    }
}

function renderSensorAccountTree(accounts, selectedAccountId = null, level = 0) {
    let html = '';
    
    accounts.forEach(account => {
        const accountId = account._id || account.id;
        const accountName = account.account_name || account.name || 'Unnamed Account';
        const hasChildren = account.children && account.children.length > 0;
        const indentPadding = level * 20;
        const isSelected = accountId === selectedAccountId;
        
        html += `
            <div class="account-tree-item" data-level="${level}">
                <div class="flex items-center py-1.5 hover:bg-gray-50 rounded" style="padding-left: ${indentPadding}px;">
                    ${hasChildren ? `
                        <button type="button" onclick="toggleSensorAccountTreeItem('sensor-tree-${accountId}')" 
                                class="mr-1 w-4 h-4 flex items-center justify-center flex-shrink-0 text-gray-400 hover:text-gray-600">
                            <svg id="sensor-tree-icon-${accountId}" class="w-4 h-4 transform transition-transform ${isSelected ? 'rotate-90' : ''}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                            </svg>
                        </button>
                    ` : '<span class="mr-1 w-4 h-4 flex-shrink-0"></span>'}
                    <label class="flex items-center cursor-pointer flex-1">
                        <input type="radio" name="sensor-account-selection" value="${accountId}" 
                               onchange="selectSensorAccount('${accountId}')"
                               ${isSelected ? 'checked' : ''}
                               class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300">
                        <span class="ml-2 text-sm text-gray-700">${accountName}</span>
                    </label>
                </div>
                ${hasChildren ? `
                    <div id="sensor-tree-${accountId}" class="${isSelected ? '' : 'hidden'}">
                        ${renderSensorAccountTree(account.children, selectedAccountId, level + 1)}
                    </div>
                ` : ''}
            </div>
        `;
    });
    
    return html;
}

export function toggleSensorAccountTreeItem(itemId) {
    const item = document.getElementById(itemId);
    const iconId = itemId.replace('sensor-tree-', 'sensor-tree-icon-');
    const icon = document.getElementById(iconId);
    
    if (item && icon) {
        item.classList.toggle('hidden');
        icon.classList.toggle('rotate-90');
    }
}

export function selectSensorAccount(accountId) {
    const createInput = document.getElementById('create-sensor-account');
    if (createInput) {
        createInput.value = accountId;
    }
    
    const editInput = document.getElementById('edit-sensor-account');
    if (editInput) {
        editInput.value = accountId;
    }

    const importInput = document.getElementById('import-sensor-account');
    if (importInput) {
        importInput.value = accountId;
    }

    const bulkInput = document.getElementById('bulk-sensor-account');
    if (bulkInput) {
        bulkInput.value = accountId;
    }
}

// ============================================================================
// Sensor Logs Modal
// ============================================================================

export async function showSensorLogsModal(sensorObjectId, sensorId) {
    try {
        // Initialize with first page
        const response = await fetch(`/api/sensor-logs?sensor_id=${sensorId}&limit=50&skip=0&sort_order=desc`, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch sensor logs');
        }

        const data = await response.json();
        console.log('📊 Sensor logs received:', data);
        
        // Handle both old array format and new object format
        const logs = Array.isArray(data) ? data : data.logs;
        const total = data.total || logs.length;
        const hasMore = data.has_more || false;
        
        console.log('📊 Logs type:', typeof logs, 'Is array:', Array.isArray(logs));
        
        const modal = document.getElementById('modal');
        console.log('📊 Modal element:', modal);
        
        if (!modal) {
            console.error('❌ Modal element not found!');
            showError('Modal element not found');
            return;
        }
        modal.innerHTML = `
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div class="bg-white rounded-lg shadow-xl max-w-6xl w-full h-[90vh] flex flex-col">
                    <!-- Header -->
                    <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div class="flex items-center justify-between mb-3">
                            <div>
                                <h3 class="text-lg font-medium text-gray-900">${t('sensors.logsTitle')}</h3>
                                <p class="text-sm text-gray-500 mt-1">Sensor-ID: ${sensorId} • I alt: ${total} logfiler</p>
                            </div>
                            <button onclick="closeModal()" class="text-gray-400 hover:text-gray-500">
                                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <!-- Date Range Filters -->
                        <div class="flex items-center space-x-3">
                            <div class="flex-1 relative">
                                <label class="block text-xs font-medium text-gray-700 mb-1">Datointerval</label>
                                <button type="button" 
                                        id="sensor-log-date-range-button"
                                        onclick="window['toggle_sensor-log-date-range-button']()"
                                        class="flex items-center justify-between w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 shadow-sm hover:bg-gray-50 text-sm">
                                    <span id="sensor-log-date-range-display" class="text-gray-700">Vælg datointerval</span>
                                    <svg class="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"/>
                                    </svg>
                                </button>
                                <input type="hidden" id="log-start-date">
                                <input type="hidden" id="log-end-date">
                                <div id="sensor-log-calendar-popup" class="hidden fixed z-50 bg-white rounded-lg shadow-lg ring-1 ring-gray-200 p-4" style="width: 320px;"></div>
                            </div>
                            <div class="pt-5">
                                <button onclick="filterSensorLogs('${sensorId}')" 
                                        class="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md">
                                    Filtrer
                                </button>
                            </div>
                            <div class="pt-5">
                                <button onclick="clearSensorLogFilters('${sensorId}')" 
                                        class="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md">
                                    Ryd
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Content: Two columns -->
                    <div class="flex-1 overflow-hidden min-h-0">
                        <div class="grid grid-cols-2 gap-4 h-full p-6">
                            <!-- Left: Log list -->
                            <div class="border border-gray-200 rounded-lg overflow-hidden flex flex-col h-full">
                                <div class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
                                    <h4 class="text-sm font-medium text-gray-900">${t('sensors.recentLogs')} (${logs.length}${hasMore ? '+' : ''})</h4>
                                </div>
                                <div id="sensor-logs-list" class="flex-1 overflow-y-auto min-h-0">
                                    ${logs.length === 0 ? 
                                        `<div class="p-4 text-center text-gray-500">${t('sensors.noLogsFound')}</div>` :
                                        logs.map((log, index) => {
                                            const date = new Date(log.sensor_timestamp || log.server_timestamp);
                                            const formattedDate = date.toLocaleDateString('en-GB');
                                            const formattedTime = date.toLocaleTimeString('en-GB');
                                            return `
                                                <button onclick="selectSensorLog('${log._id || log.id}', ${index})" 
                                                        id="log-item-${index}"
                                                        class="log-item w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-blue-50' : ''}">
                                                    <div class="flex items-center justify-between">
                                                        <div class="flex-1">
                                                            <div class="text-sm font-medium text-gray-900">${formattedDate} ${formattedTime}</div>
                                                            <div class="text-xs text-gray-500 mt-1">
                                                                ${log.location_name ? `📍 ${log.location_name}` : ''}
                                                                ${log.account_name ? `• ${log.account_name}` : ''}
                                                            </div>
                                                        </div>
                                                        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </div>
                                                </button>
                                            `;
                                        }).join('')
                                    }
                                </div>
                            </div>
                            
                            <!-- Right: JSON viewer -->
                            <div class="border border-gray-200 rounded-lg overflow-hidden flex flex-col h-full">
                                <div class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex-shrink-0">
                                    <h4 class="text-sm font-medium text-gray-900">${t('sensors.rawJsonData')}</h4>
                                </div>
                                <div id="sensor-log-json" class="flex-1 overflow-y-auto min-h-0 p-4 bg-gray-900">
                                    <pre class="text-xs text-green-400 font-mono">${logs.length > 0 ? JSON.stringify(logs[0].raw_data, null, 2) : t('sensors.selectLogToView')}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Footer -->
                    <div class="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">
                        <button onclick="closeModal()" 
                                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            ${t('common.close')}
                        </button>
                    </div>
                </div>
            </div>
        `;
        
        console.log('📊 Modal HTML set, length:', modal.innerHTML.length);
        
        // Show the modal by removing hidden class
        modal.classList.remove('hidden');
        
        // Attach scroll event listener for infinite scroll
        const logsList = document.getElementById('sensor-logs-list');
        if (logsList) {
            console.log('📜 Attaching scroll listener to logs list');
            
            logsList.addEventListener('scroll', function() {
                handleSensorLogsScroll(sensorId);
            });
        }
        
        // Store logs data for selection and pagination
        window.currentSensorLogs = logs;
        window.sensorLogsPagination = {
            sensorId: sensorId,
            skip: logs.length,
            limit: 50,
            total: total,
            hasMore: hasMore,
            loading: false,
            startDate: null,
            endDate: null
        };
        
        // Initialize sensor log calendar picker
        setTimeout(() => {
            if (!window.sensorLogCalendar) {
                window.sensorLogCalendar = createRangePicker({
                    buttonId: 'sensor-log-date-range-button',
                    displayId: 'sensor-log-date-range-display',
                    popupId: 'sensor-log-calendar-popup',
                    fromInputId: 'log-start-date',
                    toInputId: 'log-end-date',
                    onApply: () => filterSensorLogs(sensorId)
                });
                window['updateDisplay_sensor-log-date-range-button']();
            }
        }, 100);
        
        console.log('✅ Sensor logs modal displayed successfully');
        
    } catch (error) {
        console.error('❌ Error loading sensor logs:', error);
        showError(t('sensors.errorLoadingLogs') + ': ' + error.message);
    }
}

// Filter sensor logs by date range
async function filterSensorLogs(sensorId) {
    const startDate = document.getElementById('log-start-date').value;
    const endDate = document.getElementById('log-end-date').value;
    
    // Store filter values
    if (window.sensorLogsPagination) {
        window.sensorLogsPagination.startDate = startDate;
        window.sensorLogsPagination.endDate = endDate;
        window.sensorLogsPagination.skip = 0; // Reset pagination
    }
    
    // Rebuild query
    let url = `/api/sensor-logs?sensor_id=${sensorId}&limit=50&skip=0&sort_order=desc`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    
    try {
        const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${auth.getToken()}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch filtered logs');
        
        const data = await response.json();
        const logs = data.logs || data;
        
        // Update the logs list
        const logsList = document.getElementById('sensor-logs-list');
        if (logs.length === 0) {
            logsList.innerHTML = `<div class="p-4 text-center text-gray-500">${t('sensors.noLogsForDateRange')}</div>`;
        } else {
            logsList.innerHTML = logs.map((log, index) => {
                const date = new Date(log.sensor_timestamp || log.server_timestamp);
                const formattedDate = date.toLocaleDateString('en-GB');
                const formattedTime = date.toLocaleTimeString('en-GB');
                return `
                    <button onclick="selectSensorLog('${log._id || log.id}', ${index})" 
                            id="log-item-${index}"
                            class="log-item w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${index === 0 ? 'bg-blue-50' : ''}">
                        <div class="flex items-center justify-between">
                            <div class="flex-1">
                                <div class="text-sm font-medium text-gray-900">${formattedDate} ${formattedTime}</div>
                                <div class="text-xs text-gray-500 mt-1">
                                    ${log.location_name ? `📍 ${log.location_name}` : ''}
                                    ${log.account_name ? `• ${log.account_name}` : ''}
                                </div>
                            </div>
                            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </button>
                `;
            }).join('');
            
            // Update JSON viewer with first log
            if (logs.length > 0) {
                document.getElementById('sensor-log-json').innerHTML = 
                    `<pre class="text-xs text-green-400 font-mono">${JSON.stringify(logs[0].raw_data, null, 2)}</pre>`;
            }
        }
        
        // Update state
        window.currentSensorLogs = logs;
        if (window.sensorLogsPagination) {
            window.sensorLogsPagination.skip = logs.length;
            window.sensorLogsPagination.total = data.total || logs.length;
            window.sensorLogsPagination.hasMore = data.has_more || false;
        }
        
    } catch (error) {
        console.error('Error filtering logs:', error);
        showError('Failed to filter logs');
    }
}

// Clear sensor log filters
function clearSensorLogFilters(sensorId) {
    document.getElementById('log-start-date').value = '';
    document.getElementById('log-end-date').value = '';
    filterSensorLogs(sensorId); // Reload without filters
}

// Handle infinite scroll for sensor logs
async function handleSensorLogsScroll(sensorId) {
    const container = document.getElementById('sensor-logs-list');
    const pagination = window.sensorLogsPagination;
    
    if (!pagination || pagination.loading || !pagination.hasMore) {
        return;
    }
    
    // Check if scrolled near bottom (within 100px)
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    
    if (distanceFromBottom < 100) {
        pagination.loading = true;
        
        // Build URL with filters
        let url = `/api/sensor-logs?sensor_id=${sensorId}&limit=${pagination.limit}&skip=${pagination.skip}&sort_order=desc`;
        if (pagination.startDate) url += `&start_date=${pagination.startDate}`;
        if (pagination.endDate) url += `&end_date=${pagination.endDate}`;
        
        try {
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${auth.getToken()}` }
            });
            
            if (!response.ok) throw new Error('Failed to load more logs');
            
            const data = await response.json();
            const newLogs = data.logs || data;
            
            if (newLogs.length > 0) {
                // Append new logs to the list
                const currentIndex = window.currentSensorLogs.length;
                newLogs.forEach((log, index) => {
                    const date = new Date(log.sensor_timestamp || log.server_timestamp);
                    const formattedDate = date.toLocaleDateString('en-GB');
                    const formattedTime = date.toLocaleTimeString('en-GB');
                    const logHtml = `
                        <button onclick="selectSensorLog('${log._id || log.id}', ${currentIndex + index})" 
                                id="log-item-${currentIndex + index}"
                                class="log-item w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <div class="flex items-center justify-between">
                                <div class="flex-1">
                                    <div class="text-sm font-medium text-gray-900">${formattedDate} ${formattedTime}</div>
                                    <div class="text-xs text-gray-500 mt-1">
                                        ${log.location_name ? `📍 ${log.location_name}` : ''}
                                        ${log.account_name ? `• ${log.account_name}` : ''}
                                    </div>
                                </div>
                                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </div>
                        </button>
                    `;
                    container.insertAdjacentHTML('beforeend', logHtml);
                });
                
                // Update state
                window.currentSensorLogs = window.currentSensorLogs.concat(newLogs);
                pagination.skip += newLogs.length;
                pagination.hasMore = data.has_more || false;
            } else {
                pagination.hasMore = false;
            }
            
        } catch (error) {
            console.error('Error loading more logs:', error);
        } finally {
            pagination.loading = false;
        }
    }
}

function selectSensorLog(logId, index) {
    // Update selection highlighting
    document.querySelectorAll('.log-item').forEach((item, i) => {
        if (i === index) {
            item.classList.add('bg-blue-50');
            item.classList.remove('hover:bg-gray-50');
        } else {
            item.classList.remove('bg-blue-50');
            item.classList.add('hover:bg-gray-50');
        }
    });
    
    // Display the selected log's raw data
    const log = window.currentSensorLogs[index];
    const jsonViewer = document.getElementById('sensor-log-json');
    if (jsonViewer && log) {
        jsonViewer.innerHTML = `<pre class="text-xs text-green-400 font-mono">${JSON.stringify(log.raw_data, null, 2)}</pre>`;
    }
}

// Date Range Picker Utility
function createRangePicker(config) {
    const {
        buttonId,
        displayId,
        popupId,
        fromInputId,
        toInputId,
        onApply,
        singleDate = false,
        buttonText = 'Select date range'
    } = config;
    
    const state = {
        currentMonth: new Date(),
        startDate: null,
        endDate: null,
        isOpen: false,
        singleDate: singleDate
    };
    
    // Toggle calendar visibility
    window[`toggle_${buttonId}`] = function() {
        const popup = document.getElementById(popupId);
        if (!popup) return;
        
        if (state.isOpen) {
            popup.style.opacity = '0';
            popup.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                popup.classList.add('hidden');
                popup.style.opacity = '';
                popup.style.transform = '';
            }, 150);
            state.isOpen = false;
            return;
        }
        
        // Load current values
        const fromInput = document.getElementById(fromInputId);
        const toInput = document.getElementById(toInputId);
        
        if (fromInput?.value) {
            state.startDate = new Date(fromInput.value);
            state.startDate.setHours(0, 0, 0, 0);
        }
        if (!singleDate && toInput?.value) {
            state.endDate = new Date(toInput.value);
            state.endDate.setHours(0, 0, 0, 0);
        }
        
        // Set calendar month
        if (state.startDate) {
            state.currentMonth = new Date(state.startDate);
        } else {
            state.currentMonth = new Date();
        }
        
        // Position popup
        const button = document.getElementById(buttonId);
        if (button) {
            const rect = button.getBoundingClientRect();
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 4}px`;
        }
        
        state.isOpen = true;
        window[`render_${buttonId}`]();
        
        popup.style.opacity = '0';
        popup.style.transform = 'translateY(-8px)';
        popup.style.transition = 'opacity 150ms ease-out, transform 150ms ease-out';
        popup.classList.remove('hidden');
        
        requestAnimationFrame(() => {
            popup.style.opacity = '1';
            popup.style.transform = 'translateY(0)';
        });
    };
    
    // Update display text
    window[`updateDisplay_${buttonId}`] = function() {
        const display = document.getElementById(displayId);
        if (!display) return;
        
        const { startDate, endDate } = state;
        const formatDate = (d) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
        };
        
        if (singleDate && startDate) {
            display.textContent = formatDate(startDate);
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        } else if (startDate && endDate) {
            display.textContent = `${formatDate(startDate)} - ${formatDate(endDate)}`;
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        } else if (startDate) {
            display.textContent = `${formatDate(startDate)} - Select end date`;
            display.classList.remove('text-gray-400');
            display.classList.add('text-gray-700');
        } else {
            display.textContent = buttonText;
            display.classList.remove('text-gray-700');
            display.classList.add('text-gray-400');
        }
    };
    
    // Render calendar
    window[`render_${buttonId}`] = function() {
        const popup = document.getElementById(popupId);
        if (!popup) return;
        
        const year = state.currentMonth.getFullYear();
        const month = state.currentMonth.getMonth();
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = firstDay.getDay();
        const adjustedStartDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
        
        const prevMonth = new Date(year, month, 0);
        const daysInPrevMonth = prevMonth.getDate();
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let html = `
            <div class="flex items-center text-gray-900 mb-4">
                <button type="button" 
                        onclick="event.stopPropagation(); window['changeMonth_${buttonId}'](-1)"
                        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                    <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
                        <path d="M11.78 5.22a.75.75 0 010 1.06L8.06 10l3.72 3.72a.75.75 0 11-1.06 1.06l-4.25-4.25a.75.75 0 010-1.06l4.25-4.25a.75.75 0 011.06 0z"/>
                    </svg>
                </button>
                <div class="flex-auto text-sm font-semibold text-center">${monthNames[month]} ${year}</div>
                <button type="button" 
                        onclick="event.stopPropagation(); window['changeMonth_${buttonId}'](1)"
                        class="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                    <svg viewBox="0 0 20 20" fill="currentColor" class="size-5">
                        <path d="M8.22 5.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 010-1.06z"/>
                    </svg>
                </button>
            </div>
            <div class="grid grid-cols-7 text-xs text-center font-semibold text-gray-500 mb-2">
                <div>M</div><div>T</div><div>W</div><div>T</div><div>F</div><div>S</div><div>S</div>
            </div>
            <div class="grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
        `;
        
        // Helper functions
        const isInRange = (dateObj) => {
            if (singleDate || !state.startDate || !state.endDate) return false;
            return dateObj >= state.startDate && dateObj <= state.endDate;
        };
        const isRangeStart = (dateObj) => state.startDate && dateObj.getTime() === state.startDate.getTime();
        const isRangeEnd = (dateObj) => state.endDate && dateObj.getTime() === state.endDate.getTime();
        
        // Previous month days
        for (let i = adjustedStartDay - 1; i >= 0; i--) {
            const day = daysInPrevMonth - i;
            html += `<button type="button" class="py-1.5 bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors duration-150"
                     onclick="event.stopPropagation(); window['selectDate_${buttonId}'](${year}, ${month - 1}, ${day})">${day}</button>`;
        }
        
        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const dateObj = new Date(year, month, day);
            dateObj.setHours(0, 0, 0, 0);
            
            const isToday = dateObj.getTime() === today.getTime();
            const isStart = isRangeStart(dateObj);
            const isEnd = isRangeEnd(dateObj);
            const inRange = isInRange(dateObj);
            
            let classes = 'py-1.5 bg-white hover:bg-gray-100 transition-colors duration-150';
            if (isStart || isEnd) {
                classes = 'py-1.5 font-semibold text-white bg-primary-600 hover:bg-primary-500 transition-colors duration-150';
            } else if (inRange) {
                classes = 'py-1.5 bg-primary-100 hover:bg-primary-200 text-primary-900 transition-colors duration-150';
            } else if (isToday) {
                classes += ' font-semibold text-primary-600';
            }
            
            html += `<button type="button" class="${classes}"
                     onclick="event.stopPropagation(); window['selectDate_${buttonId}'](${year}, ${month}, ${day})">${day}</button>`;
        }
        
        // Next month days
        const totalCells = adjustedStartDay + daysInMonth;
        const remainingCells = 42 - totalCells;
        for (let day = 1; day <= remainingCells && day <= 14; day++) {
            html += `<button type="button" class="py-1.5 bg-gray-50 text-gray-400 hover:bg-gray-100 transition-colors duration-150"
                     onclick="event.stopPropagation(); window['selectDate_${buttonId}'](${year}, ${month + 1}, ${day})">${day}</button>`;
        }
        
        html += `</div>`;
        
        if (!singleDate) {
            html += `<div class="mt-3 text-xs text-center text-gray-500 mb-2">
                ${state.startDate && !state.endDate ? 'Select end date' : 'Click to select start date, then end date'}
            </div>`;
        }
        
        html += `
            <div class="flex gap-2 mt-3">
                <button type="button" onclick="event.stopPropagation(); window['clear_${buttonId}']()"
                        class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150">Clear</button>
                <button type="button" onclick="event.stopPropagation(); window['setToday_${buttonId}']()"
                        class="flex-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-150">Today</button>
                <button type="button" onclick="event.stopPropagation(); window['apply_${buttonId}']()"
                        class="flex-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 transition-colors duration-150">Apply</button>
            </div>
        `;
        
        popup.innerHTML = html;
    };
    
    // Select date
    window[`selectDate_${buttonId}`] = function(year, month, day) {
        const selectedDate = new Date(year, month, day);
        selectedDate.setHours(0, 0, 0, 0);
        
        if (singleDate) {
            state.startDate = selectedDate;
            state.endDate = null;
        } else {
            if (!state.startDate || selectedDate < state.startDate) {
                state.startDate = selectedDate;
                state.endDate = null;
            } else if (!state.endDate) {
                state.endDate = selectedDate;
            } else {
                state.startDate = selectedDate;
                state.endDate = null;
            }
        }
        
        window[`updateDisplay_${buttonId}`]();
        window[`render_${buttonId}`]();
    };
    
    // Change month
    window[`changeMonth_${buttonId}`] = function(delta) {
        state.currentMonth = new Date(state.currentMonth.getFullYear(), state.currentMonth.getMonth() + delta, 1);
        window[`render_${buttonId}`]();
    };
    
    // Set today
    window[`setToday_${buttonId}`] = function() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        state.startDate = today;
        state.endDate = singleDate ? null : today;
        window[`updateDisplay_${buttonId}`]();
        window[`render_${buttonId}`]();
    };
    
    // Clear selection
    window[`clear_${buttonId}`] = function() {
        state.startDate = null;
        state.endDate = null;
        window[`updateDisplay_${buttonId}`]();
        window[`render_${buttonId}`]();
    };
    
    // Apply selection
    window[`apply_${buttonId}`] = function() {
        if (!state.startDate) return;
        
        const finalStartDate = state.startDate;
        const finalEndDate = singleDate ? null : (state.endDate || state.startDate);
        
        const fromInput = document.getElementById(fromInputId);
        const toInput = document.getElementById(toInputId);
        
        if (fromInput) fromInput.value = finalStartDate.toISOString().split('T')[0];
        if (toInput && !singleDate) toInput.value = finalEndDate.toISOString().split('T')[0];
        
        const popup = document.getElementById(popupId);
        if (popup) {
            popup.style.opacity = '0';
            popup.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                popup.classList.add('hidden');
                popup.style.opacity = '';
                popup.style.transform = '';
            }, 150);
        }
        state.isOpen = false;
        
        if (onApply) onApply();
    };
    
    return state;
}

// ============================================================================
// Export Sensors Modal (Stub)
// ============================================================================

export function showExportSensorsModal() {
    console.log('Export Sensors Modal - Coming soon');
    alert('Eksporter Sensorer funktionalitet kommer snart');
}

// ============================================================================
// Global Exports (for onclick handlers)
// ============================================================================

window.showCreateSensorModal = showCreateSensorModal;
window.handleCreateSensor = handleCreateSensor;
window.onCreateSensorTypeChange = (value) => {
    const wrapper = document.getElementById('create-sensor-mac-wrapper');
    if (wrapper) wrapper.classList.toggle('hidden', value !== 'temp_humidity');
};
window.showImportSensorsModal = showImportSensorsModal;
window.handleImportSensors = handleImportSensors;
window.showEditSensorModal = showEditSensorModal;
window.showSensorLogsModal = showSensorLogsModal;
window.showExportSensorsModal = showExportSensorsModal;
window.toggleSensorAccountTreeItem = toggleSensorAccountTreeItem;
window.selectSensorAccount = selectSensorAccount;
window.filterSensorLogs = filterSensorLogs;
window.clearSensorLogFilters = clearSensorLogFilters;
window.selectSensorLog = selectSensorLog;
